<template>
  <div v-if="isVisible" class="playlist-modal-overlay" @click="handleOverlayClick">
    <div class="playlist-modal" @click.stop>
      <div class="playlist-header">
        <h2>播放列表</h2>
        <button class="close-button" @click="$emit('close')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      
      <div class="playlist-content">
        <div v-if="allTracks.length === 0" class="empty-playlist">
          <p>暂无音乐</p>
        </div>
        <div v-else class="track-list">
          <div 
            v-for="(track, index) in allTracks" 
            :key="index"
            class="track-item"
            :class="{ 'current': index === currentTrackIndex }"
            @click="playTrack(index)"
          >
            <div class="track-play-icon">
              <svg v-if="index === currentTrackIndex && isPlaying" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polygon points="5,3 19,12 5,21" fill="currentColor"/>
              </svg>
            </div>
            <div class="track-info">
              <div class="track-title">{{ track.title }}</div>
              <div class="track-artist">{{ track.artist }}</div>
            </div>
            <div class="track-duration">{{ track.duration || '未知' }}</div>
            <button 
              v-if="track.isUploaded"
              class="remove-button"
              @click.stop="removeTrack(index)"
              title="删除"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="3,6 5,6 21,6"/>
                <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2v2"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlaylistModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    allTracks: {
      type: Array,
      default: () => []
    },
    currentTrackIndex: {
      type: Number,
      default: 0
    },
    isPlaying: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'play-track', 'remove-track'],
  setup(props, { emit }) {
    const handleOverlayClick = () => {
      emit('close')
    }
    
    const playTrack = (index) => {
      emit('play-track', index)
    }
    
    const removeTrack = (index) => {
      emit('remove-track', index)
    }
    
    return {
      handleOverlayClick,
      playTrack,
      removeTrack
    }
  }
}
</script>

<style scoped>
.playlist-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

/* 弹窗全屏确保 - #app已经突破safe area，这里只需保证完全覆盖 */
@supports (env(safe-area-inset-top)) {
  .playlist-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
  }
}

.playlist-modal {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  color: white;
  display: flex;
  flex-direction: column;
}

.playlist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
}

.playlist-header h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
}

.close-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: white;
  background: rgba(255, 255, 255, 0.1);
}

.close-button svg {
  width: 20px;
  height: 20px;
}

.playlist-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.empty-playlist {
  padding: 40px 25px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.track-list {
  max-height: 400px;
  overflow-y: auto;
}

.track-item {
  display: flex;
  align-items: center;
  padding: 12px 25px;
  transition: all 0.3s ease;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.track-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.track-item.current {
  background: rgba(255, 255, 255, 0.15);
  border-left: 3px solid rgba(255, 255, 255, 0.8);
}

.track-play-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  flex-shrink: 0;
}

.track-play-icon svg {
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.8);
}

.track-item.current .track-play-icon svg {
  color: white;
}

.track-info {
  flex: 1;
  min-width: 0;
  margin-right: 15px;
}

.track-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 2px;
}

.track-item.current .track-title {
  color: white;
}

.track-artist {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.track-duration {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
  margin-right: 10px;
  flex-shrink: 0;
}

.remove-button {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 6px;
  border-radius: 50%;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.remove-button:hover {
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
}

.remove-button svg {
  width: 16px;
  height: 16px;
}

/* 滚动条样式 */
.track-list::-webkit-scrollbar {
  width: 6px;
}

.track-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.track-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.track-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

/* 移动端响应式 */
@media (max-width: 768px) {
  .playlist-modal {
    width: 95%;
    max-height: 85vh;
  }
  
  .playlist-header {
    padding: 15px 20px;
  }
  
  .playlist-header h2 {
    font-size: 1.3rem;
  }
  
  .track-item {
    padding: 10px 20px;
  }
  
  .track-info {
    margin-right: 10px;
  }
}

@media (max-width: 480px) {
  .playlist-modal {
    width: 98%;
    max-height: 90vh;
  }
  
  .playlist-header {
    padding: 12px 15px;
  }
  
  .track-item {
    padding: 8px 15px;
  }
  
  .track-play-icon {
    width: 25px;
    height: 25px;
    margin-right: 10px;
  }
  
  .track-duration {
    display: none;
  }
}
</style>